import { Select, MenuItem, Typography, Box, TextField, Alert, AlertTitle, Skeleton, ListSubheader } from '@mui/material';
import { default as TeamsIcon } from '@mui/icons-material/Group';
import Grid from "@mui/material/Grid2";
import React from 'react';
import _ from 'lodash';

import { useOrgConfig, VisitResourceRequirement } from "~/providers/OrgConfigProvider";
import { orJoin, UNASSIGNED_PROVIDER_ID } from "~/utils/visitsHelper";
import { CommandIndexProvidersQuery } from '~/graphql/__generated__/graphql';
import { 
  getAssignableProvidersForRequirement,
  SelectProviderData,
  defaultProviderData,
  hasStateLicense
} from '~/utils/visitValidationUtils';
import { applyCareTeamToProviderData, CareTeamProvider, getAllowedCareTeamProviders, isMissingCareTeam } from '~/utils/careTeamHelpers';
import { CareTeamWarning } from '../common/CareTeamWarning';
import {
  groupByTeam,
  hasTeamMismatch,
  ProviderSelectItem
} from '~/utils/providerTeamHelpers';

/**
 * Controls the per-requirement offset and duration fields:
 * - `never`: hidden, even for multi-resource visits
 * - `whenMultipleRequirements`: shown only when there is more than one requirement to stagger
 * - `always`: shown even for a single requirement
 */
export type OffsetFieldsVisibility = 'never' | 'whenMultipleRequirements' | 'always';

interface SelectProviderProps {
  loading: boolean;
  visitResourceRequirements: VisitResourceRequirement[];
  providersList: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  prepopulatedProviderList: SelectProviderData[];
  setProviderData: (data: SelectProviderData[]) => void;
  shifts: CommandIndexProvidersQuery["shift"];
  startTime: string;
  endTime: string;
  patient: {
    id: number,
    firstName: string,
    lastName: string,
    addressState: string | null,
    careTeamProviders: CareTeamProvider[],
  };
  offsetFieldsVisibility?: OffsetFieldsVisibility;
  requirementLabels?: Record<number, string>;
  showAssignmentWarnings?: boolean;
  timezone?: string | null;
  strictShiftFiltering?: boolean;
}

const ProviderSelect: React.FC<SelectProviderProps> = ({ 
  loading,
  visitResourceRequirements, 
  providersList, 
  prepopulatedProviderList,
  setProviderData,
  shifts,
  startTime,
  endTime,
  patient,
  offsetFieldsVisibility = 'whenMultipleRequirements',
  requirementLabels,
  showAssignmentWarnings = true,
  timezone,
  strictShiftFiltering = true
}) => {
  const orgConfig = useOrgConfig();

  const careTeamProviders = getAllowedCareTeamProviders(
    visitResourceRequirements,
    patient.careTeamProviders,
    providersList,
  );

  // Filter providers by requirements and shift compatibility
  const providersBySlot: ProviderSelectItem[][] = visitResourceRequirements?.map((vrr) => {
    const filteredProviders = getAssignableProvidersForRequirement(
      providersList,
      shifts,
      vrr,
      startTime,
      endTime,
      timezone,
      strictShiftFiltering
    );
    return filteredProviders.map((provider) => ({
      value: provider.id,
      label: `${provider.firstName} ${provider.lastName}`,
      teamProviders: provider.teamProviders?.map(tp => ({ teamId: tp.teamId })),
    }));
  }) || [];


  const isMissingCareTeamComputed = isMissingCareTeam({
    careTeamProviders,
    selectedProviders: prepopulatedProviderList.map(pd => Number(pd.resourceId))
  });

  const requirementIds = new Set(visitResourceRequirements.map(vrr => vrr.id));

  // Get list of unlicensed providers with their names
  const unlicensedProviders = (() => {
    if (!patient.addressState) return [];
    
    return prepopulatedProviderList
      .filter(pd => requirementIds.has(pd._vrrId) && pd.resourceId && pd.resourceId !== '' && pd.resourceId !== UNASSIGNED_PROVIDER_ID.toString())
      .map(pd => {
        const provider = providersList.find((p: CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]) => p.id === Number(pd.resourceId));
        if (!provider) return null;
        
        const providerType = provider.providerTypeId ? orgConfig.providerTypes[provider.providerTypeId] : null;
        const requiresLicense = (providerType as { requireStateLicense?: boolean })?.requireStateLicense;
        
        if (!requiresLicense) return null;
        
        const providerTraits = (provider as { traits?: { trait: string; value: string }[] }).traits;
        if (!hasStateLicense(providerTraits, patient.addressState)) {
          return {
            id: provider.id,
            firstName: provider.firstName,
            lastName: provider.lastName,
          };
        }
        return null;
      })
      .filter(Boolean) as { id: number; firstName: string | null; lastName: string | null }[];
  })();

  const shouldShowOffsets = offsetFieldsVisibility === 'always'
    || (offsetFieldsVisibility === 'whenMultipleRequirements' && visitResourceRequirements.length > 1);

  // Helper function to update provider data for a specific requirement
  const updateProviderData = (vrrId: number, updates: Partial<SelectProviderData>) => {
    const newData = [...prepopulatedProviderList];
    const providerDataIndex = prepopulatedProviderList.findIndex(pd => pd._vrrId === vrrId);
    const currentData = prepopulatedProviderList.find(pd => pd._vrrId === vrrId) || { ...defaultProviderData, _vrrId: vrrId };
    
    if (providerDataIndex !== -1) {
      // Update existing entry
      newData[providerDataIndex] = { ...currentData, ...updates };
    } else {
      // Create new entry
      newData.push({ ...currentData, ...updates, _vrrId: vrrId });
    }
    
    setProviderData(newData);
  };

  // Apply care team function
  const applyCareTeam = () => {
    const newProviderData = applyCareTeamToProviderData({
      providerData: prepopulatedProviderList,
      careTeamProviders,
      visitResourceRequirements,
      availableProvidersBySlot: providersBySlot
    });
    setProviderData(newProviderData);
  };

  // Handle provider selection
  const handleProviderChange = (vrr: VisitResourceRequirement, providerId: string) => {
    updateProviderData(vrr.id, { resourceId: providerId });
  };

  return (
    <Box sx={{ my: 2 }}>
      {loading && visitResourceRequirements.map((vrr) => (
        <Skeleton key={vrr.id} sx={{ my: 2 }} variant="rectangular" height={20} />
      ))}
      {!loading && visitResourceRequirements?.map((vrr, index) => {
        const currentData = prepopulatedProviderList.find(pd => pd._vrrId === vrr.id) || { ...defaultProviderData, _vrrId: vrr.id };
        const availableProviders = providersBySlot[index] || [];
        const groupedProviders = groupByTeam(availableProviders, vrr.id, prepopulatedProviderList, providersList, orgConfig);

        const assignedProviderId = currentData.resourceId;
        
        return (
          <Box sx={{ my: 2 }} key={`${vrr.id}`}>
            <Grid container spacing={2} alignItems="center" data-testid={`provider-select-${vrr.id}`}>
              <Grid size={{ xs: 12, sm: shouldShowOffsets ? 6 : 12 }}>
                <Typography variant='subtitle2'>{requirementLabels?.[vrr.id] ?? orJoin(vrr.providerTypes.map(pt => _.startCase(pt.name)))}</Typography>
                {availableProviders.length > 0 ? (
                  <Select
                    name={`provider_select_${vrr.id}`}
                    size="small"
                    value={assignedProviderId || ""}
                    onChange={e => {
                      handleProviderChange(vrr, e.target.value.toString());
                    }}
                    displayEmpty
                    sx={{ width: '100%', maxWidth: '330px' }}
                  >
                    <MenuItem value="">None</MenuItem>

                    {groupedProviders.teamMatches.length > 0 && <ListSubheader><TeamsIcon sx={{ fontSize: 16, color: 'primary.main', verticalAlign: 'middle', marginRight: 1 }} />Typically works with {groupedProviders.referenceProviderName}</ListSubheader>}
                    {groupedProviders.teamMatches.map(p => (
                      <MenuItem 
                        key={p.value} 
                        value={p.value.toString()}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>{p.label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                    {groupedProviders.noTeamMatches.length > 0 && groupedProviders.teamMatches.length > 0 && <ListSubheader>Everyone else</ListSubheader>}
                    {groupedProviders.noTeamMatches.map(p => (
                      <MenuItem 
                        key={p.value} 
                        value={p.value.toString()}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <span>{p.label}</span>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Alert severity="warning">
                    <AlertTitle>No Providers Available</AlertTitle>
                    No available shifts for {orJoin(vrr.providerTypes.map(pt => _.startCase(pt.name)))}, please create missing shifts.
                  </Alert>
                )}
              </Grid>
              {shouldShowOffsets && (
                <>
                  <Grid size={{ xs: 6, sm: 3 }} data-testid={`provider_row_${vrr.id}_offset`}>
                    <Typography variant='subtitle2'>Offset (minutes)</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={currentData.offset || 0}
                      onChange={e => {
                        updateProviderData(vrr.id, { offset: parseInt(e.target.value) || 0 });
                      }}
                      sx={{ width: '100%' }}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 6, sm: 3 }} data-testid={`provider_row_${vrr.id}_duration`}>
                    <Typography variant='subtitle2'>Duration (minutes)</Typography>
                    <TextField
                      size="small"
                      type="number"
                      value={currentData.duration || 0}
                      onChange={e => {
                        updateProviderData(vrr.id, { duration: parseInt(e.target.value) || 0 });
                      }}
                      sx={{ width: '100%' }}
                      slotProps={{ htmlInput: { min: 0 } }}
                    />
                  </Grid>
                </>
              )}
            </Grid>
          </Box>
        );
      })}

      {!loading && showAssignmentWarnings && isMissingCareTeamComputed && (
        <CareTeamWarning patient={patient} careTeamProviders={careTeamProviders} applyCareTeam={applyCareTeam} />
      )}

      {!loading && showAssignmentWarnings && hasTeamMismatch(visitResourceRequirements, prepopulatedProviderList, orgConfig) && (
        <Alert severity="warning" sx={{ mb: 2, mt: 2, fontSize: '0.875rem' }}>
          <AlertTitle sx={{ fontSize: '0.875rem' }}>Provider Team Warning</AlertTitle>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            The selected providers don't share a team. Consider selecting providers who work together for better care coordination.
          </Typography>
        </Alert>
      )}

      {!loading && unlicensedProviders.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2, mt: 2, fontSize: '0.875rem' }}>
          <AlertTitle sx={{ fontSize: '0.875rem' }}>State License Warning</AlertTitle>
          <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
            {unlicensedProviders.map(p => `${p.firstName} ${p.lastName}`).join(', ')} {unlicensedProviders.length === 1 ? 'is' : 'are'} not licensed in {patient.addressState}.
          </Typography>
        </Alert>
      )}

    </Box>
  );
};

export default ProviderSelect;
